import mqtt from 'mqtt';
import { BehaviorSubject, EMPTY, Observable, Observer, Subject } from 'rxjs';

import { buffer, bufferTime, catchError, filter, map, mergeMap, retry, withLatestFrom } from 'rxjs/operators';

import { ConnectionStatus, ConnectionStatusType, type SocketLog } from './socketMessage';
import { timestampToDate } from './socketUtils';

type SocketOption = mqtt.IClientOptions;

declare type Deserializer = {
  decode: (raw: any) => any;
  decodeKrx: (raw: any, topic: string) => any;
};

export enum ConnectSocketMessage {
  CONNECT_SOCKET_FAILED = 'CONNECT_SOCKET_FAILED',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
}

type DeserializedMqttMsg = {
  retained: boolean;
  topic: string;
  type: string;
  payload: { clientReceivedTime: string } & Record<string, any>;
};

export type LatencyLogStream = { topic: string; latency: number }[];

// TODO: Prevent duplicated subscribe, implement back-off retry mechanism, add unit test, make code
// less duplicate
export class SocketService {
  private socketClient?: mqtt.MqttClient;

  private subscribe$: Subject<any>;

  private unsubscribe$: Subject<string[]>;

  private message$: Subject<DeserializedMqttMsg>;

  private messageLatency$: BehaviorSubject<DeserializedMqttMsg>;

  private log$: BehaviorSubject<SocketLog>;

  private connectionStatus$: BehaviorSubject<ConnectionStatusType>;

  private reconnectPeriod: number;

  private deserializer: Deserializer = {
    decode() {},
    decodeKrx() {},
  };

  constructor() {
    this.subscribe$ = new Subject();
    this.unsubscribe$ = new Subject();
    this.connectionStatus$ = new BehaviorSubject<ConnectionStatusType>(ConnectionStatus.DISCONNECT);
    this.message$ = new Subject();
    this.messageLatency$ = new BehaviorSubject({} as DeserializedMqttMsg);
    this.log$ = new BehaviorSubject<SocketLog>({} as SocketLog);
    this.handleSubscribeStream();
    this.handleUnsubscribeStream();
    this.reconnectPeriod = 1000;
  }

  public setDeserializer(des: Deserializer) {
    this.deserializer = des;
  }

  log({ level = 'info', extra, message }: SocketLog) {
    this.log$.next({ message, level, extra });
    console.log(message, extra);
  }

  connect(uri: string, opts: SocketOption) {
    this.socketClient = mqtt.connect(uri, {
      username: opts.username,
      password: opts.password,
      protocolVersion: 5,
      reconnectPeriod: this.reconnectPeriod,
      clientId: opts.clientId,
    });

    this.initListener();
  }

  handleSubscribeStream() {
    // When socket is connected
    this.subscribe$
      .asObservable()
      .pipe(
        withLatestFrom(this.connectionStatus$),
        filter(([topics, status]) => status === ConnectionStatus.CONNECT),
        map(([topics, _]) => topics),
        bufferTime(500),
        filter(list => list.length > 0),
        mergeMap(topics =>
          this.subscriptionResultObservable(topics).pipe(
            retry(2),
            catchError(err => {
              if (this.connectionStatus$.getValue() !== ConnectionStatus.END) {
                this.subscribe$.next(topics);
              } else {
                console.log('Stop trying because user is out');
              }
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();

    this.subscribe$
      .asObservable()
      .pipe(
        withLatestFrom(this.connectionStatus$),
        filter(([topics, status]) => status !== ConnectionStatus.CONNECT && status !== ConnectionStatus.END),
        map(([topics, _]) => topics),
        buffer(this.connectionStatus$.pipe(filter(status => status === ConnectionStatus.CONNECT))),
        filter(list => list.length > 0),
        mergeMap(topics =>
          this.subscriptionResultObservable(topics).pipe(
            retry(2),
            catchError(err => {
              if (this.connectionStatus$.getValue() !== ConnectionStatus.END) {
                this.subscribe$.next(topics);
              } else {
                console.log('Stop trying because user is out');
              }
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  disconnect() {
    this.socketClient?.end(true, undefined, err => {
      if (err) {
        console.error('Cant disconnect socket properly:', err);
      } else {
        console.info('Disconnect socket success');
      }
    });
  }

  async connectAsync(uri: string, opts) {
    return mqtt.connectAsync(uri, {
      username: opts.username,
      password: opts.password,
      protocolVersion: 5,
      reconnectPeriod: this.reconnectPeriod,
      clientId: opts.clientId,
    });
  }

  async reconnectWithNewUri(uri: string, opts, topics: string[]) {
    try {
      await this.socketClient?.endAsync();
      console.info('Disconnect socket success');
    } catch (err) {
      console.error('Cant disconnect socket properly:', err);
    }
    mqtt
      .connectAsync(uri, {
        username: opts.username,
        password: opts.password,
        protocolVersion: 5,
        reconnectPeriod: this.reconnectPeriod,
        clientId: opts.clientId,
      })
      .then(res => {
        this.socketClient = res;
        this.initListener();
        this.connectionStatus$.next(ConnectionStatus.CONNECT);
        this.subscribe(topics);
      })
      .catch(err => {
        console.error('Cant connect socket properly:', err);
        this._handleConnectionError(err);
      });
  }

  initListener() {
    this.socketClient?.on('connect', packet => {
      this.connectionStatus$.next(ConnectionStatus.CONNECT);
    });

    this.socketClient?.on('error', err => this._handleConnectionError(err));

    this.socketClient?.on('message', (topic: string, payload, pack) => {
      try {
        const msg =
          topic.includes('krx') && !topic.includes('ohlc')
            ? this.deserializer.decodeKrx(pack.payload, topic)
            : this.deserializer.decode(pack.payload);
        this.message$.next({ ...msg, retained: pack.retain, topic: pack.topic });
        if (
          topic.includes('quotes/stock') ||
          topic.includes('quotes/derivative') ||
          topic.includes('quotes/krx/mdds')
        ) {
          this.messageLatency$.next({ ...msg, retained: pack.retain, topic: pack.topic });
        }
      } catch (error) {
        this.log({ message: 'Receive message observable err', level: 'error', extra: error });
      }
    });
    this.socketClient?.on('close', () => {
      console.info('CLOSE SOCKET');
      this.connectionStatus$.next(ConnectionStatus.CLOSE);
    });
    this.socketClient?.on('reconnect', () => {
      console.info('RECONNECT SOCKET');
      this.connectionStatus$.next(ConnectionStatus.RECONNECT);
    });

    this.socketClient?.on('disconnect', msg => {
      console.info('DISCONNECT SOCKET', msg);
      this.connectionStatus$.next(ConnectionStatus.DISCONNECT);
    });

    this.socketClient?.on('end', () => {
      console.info('END SOCKET');
      this.connectionStatus$.next(ConnectionStatus.END);
    });
  }

  subscribe(topics: string[]) {
    if (topics.length > 0) {
      topics.forEach(topic => {
        this.subscribe$.next(topic);
      });
    }
  }

  subscriptionResultObservable(topics: string[]) {
    return new Observable((observer: Observer<any>) => {
      if (this.socketClient?.connected && !this.socketClient.disconnecting) {
        this.socketClient?.subscribe([...new Set(topics)], (err, grant) => {
          if (err) {
            observer.error(err);
          } else {
            observer.next(grant);
          }
        });
      } else {
        observer.error('Socket is not connected');
      }
    });
  }

  handleUnsubscribeStream() {
    this.unsubscribe$
      .asObservable()
      .pipe(
        withLatestFrom(this.connectionStatus$),
        filter(([topics, status]) => status === ConnectionStatus.CONNECT),
        map(([topics, _]) => topics),
        filter(list => list.length > 0),
        mergeMap(topics => {
          if (this.socketClient?.connected && !this.socketClient.disconnecting) {
            this.socketClient?.unsubscribe([...new Set(topics)], err => {
              if (err) {
                console.error('Cannot unsubscribe: ', err.message);
              }
            });
          } else {
            console.error('Cannot unsubscribe: Socket is not connected');
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  unsubscribe(topics: string[]) {
    if (topics.length > 0) {
      this.unsubscribe$.next(topics);
    }
  }

  private _handleConnectionError(err) {
    let message = ConnectSocketMessage.CONNECT_SOCKET_FAILED;
    /** @ts-expect-error error does have code props
     * unauthorized code: https://docs.emqx.com/en/cloud/latest/connect_to_deployments/mqtt_client_error_codes.html#connack-packet
     */
    if ([133, 134, 135].includes(err?.code)) {
      message = ConnectSocketMessage.NOT_AUTHORIZED;
      this.reconnectPeriod = 0;
    }
    this.log({
      message,
      level: 'error',
      extra: JSON.stringify({ ...this.socketClient?.options, error: err }),
    });
    this.connectionStatus$.next(ConnectionStatus.DISCONNECT);
  }

  streamMessage() {
    return this.message$.asObservable();
  }

  streamConnectionStatus() {
    return this.connectionStatus$.asObservable();
  }

  streamLog() {
    return this.log$.asObservable();
  }

  /**
   * Stream message with latency
   * only process price message: SI, TP, TICK
   * Buffer in 60s, return average latency by message type
   */
  streamLatencyLog(): Observable<LatencyLogStream> {
    const trackTopics = ['STOCK_INFO', 'TOP_PRICE', 'MARKET_INDEX', 'TICK', 'DERIVATIVE_PRICE'];

    return this.messageLatency$.pipe(
      filter(msg => !!msg.payload && !msg.retained),
      bufferTime(60000),
      filter(messages => messages.length > 0),
      map(messages => {
        const begin = performance.now();
        const groupedMessages: Record<string, DeserializedMqttMsg[]> = {};
        messages.forEach(({ type, payload, ...rest }) => {
          if (trackTopics.includes(type)) {
            const floorCode = payload.floorCode || payload.marketId || '';
            const groupName = floorCode ? `${type}/${floorCode}` : type;
            if (!groupedMessages[groupName]) {
              groupedMessages[groupName] = [];
            }
            groupedMessages[groupName].push({ type: groupName, payload, ...rest });
          }
        });

        return Object.entries(groupedMessages).map(([key, _messages]) => {
          const latency = _messages.reduce((acc, { payload }) => {
            const clientReceivedTime = new Date(payload.clientReceivedTime);
            const createdDate = payload.receivedTime;
            const diff = createdDate
              ? clientReceivedTime.getTime() - (timestampToDate(createdDate)?.getTime() ?? clientReceivedTime.getTime())
              : 0;
            return acc + diff;
          }, 0);

          return {
            topic: key,
            latency: latency / _messages.length,
            numberOfMessages: _messages.length,
            calculationTime: performance.now() - begin,
          };
        });
      }),
    );
  }
}
